import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./learnerAuth";
import { resolveLmsShortName, learnerPath } from "./_learnerConfig";
import { getCachedPortalInfo, loadPortalInfoCached } from "./learnerPortalInfoCache";
import { SUPPORTED_LANGUAGES } from "../i18n/languages";
import { loadLanguage } from "../i18n/i18n";


// Layout shell for the authenticated learner portal: top bar with brand,
// primary nav (Dashboard / My Courses / Profile / Support) and logout.
// Calm, course-focused, responsive — no marketing chrome.
const LearnerShell = ({ title, children, wide = false }) => {
  const { t, i18n } = useTranslation();
  const { learner, logout } = useAuth();
  const navigate = useNavigate();
  const { orgName } = useParams();
  const [organisation, setOrganisation] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);

  const fallbackBrandName = "ESHS Learning";
  const fallbackBrandLogo = "/assets/icons/eC-Card-Logo.svg";
  const lmsShortName = orgName || resolveLmsShortName();
  const cachedPortalInfo = getCachedPortalInfo(lmsShortName);
  const cachedOrganisation = cachedPortalInfo?.organisation || null;
  const visibleOrganisation = organisation || cachedOrganisation;

  useEffect(() => {
    let active = true;

    const loadOrganisation = async () => {
      if (!lmsShortName) return;
      try {
        const data = await loadPortalInfoCached(lmsShortName);
        if (active) {
          setOrganisation(data?.organisation || null);
        }
      } catch {
        if (active && !getCachedPortalInfo(lmsShortName)?.organisation) setOrganisation(null);
      }
    };

    loadOrganisation();
    return () => {
      active = false;
    };
  }, [lmsShortName]);

  useEffect(() => {
    if (!mobileMenuOpen) return undefined;
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen]);

  const doLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate(learnerPath(lmsShortName, "login"));
  };

  const switchLang = async (event) => {
    const lang = event.target.value;
    localStorage.setItem("userLanguage", lang);
    await loadLanguage(lang);
    i18n.changeLanguage(lang);
    window.location.reload();
  };

  const links = [
    { to: learnerPath(orgName, "dashboard"), icon: "fa-home", label: t("learner.lms.nav.dashboard", "Dashboard") },
    { to: learnerPath(orgName, "my-courses"), icon: "fa-book-open", label: t("learner.lms.nav.myCourses", "My Courses") },
    { to: learnerPath(orgName, "certificates"), icon: "fa-certificate", label: t("learner.lms.nav.certificates", "My Certificates") },
    { to: learnerPath(orgName, "profile"), icon: "fa-user", label: t("learner.lms.nav.profile", "Profile") },
    { to: learnerPath(orgName, "help&support"), icon: "fa-question-circle", label: t("learner.lms.nav.support", "Help & Support") },
  ];

  const name = learner?.firstName || learner?.name || "";
  const brandName = visibleOrganisation?.name || fallbackBrandName;
  const brandLogo = visibleOrganisation?.brandLogoUrl || fallbackBrandLogo;

  return (
    <>
      <Helmet>
        <title>{title ? `${title} - ${brandName}` : brandName}</title>
      </Helmet>
      <div className="lms-portal">
        <header className="lms-portal-topbar">
          <div className="lms-portal-brand">
            <img
              src={brandLogo}
              alt=""
              className="lms-portal-logo"
              onError={(event) => {
                if (event.currentTarget.src !== window.location.origin + fallbackBrandLogo) {
                  event.currentTarget.src = fallbackBrandLogo;
                }
              }}
            />
            <span className="lms-portal-brandname">{brandName}</span>
          </div>
          <button
            ref={menuButtonRef}
            className="lms-portal-menu-btn"
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label={t("learner.lms.nav.openMenu", "Open menu")}
            aria-expanded={mobileMenuOpen}
          >
            <i className="fas fa-bars" aria-hidden="true"></i>
          </button>
          <div
            className={`lms-portal-drawer-backdrop ${mobileMenuOpen ? "show" : ""}`}
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
          <nav
            className={`lms-portal-nav ${mobileMenuOpen ? "show" : ""}`}
            aria-label={t("learner.lms.nav.main", "Main")}
          >
            <div className="lms-portal-drawer-head">
              <div className="lms-portal-drawer-brand">
                <img src={brandLogo} alt="" className="lms-portal-drawer-logo" />
                <span>{brandName}</span>
              </div>
              <button
                ref={closeButtonRef}
                className="lms-portal-drawer-close"
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label={t("common.close", "Close")}
              >
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `lms-portal-navlink ${isActive ? "active" : ""}`
                }
              >
                <i className={`fas ${l.icon}`} aria-hidden="true"></i>
                <span>{l.label}</span>
              </NavLink>
            ))}
            <div className="lms-portal-drawer-user">
              <div className="lms-portal-language lms-portal-language-mobile">
                {/* <select
                  className="policy-language-select lms-portal-policy-language-select"
                  value={i18n.language || localStorage.getItem("userLanguage") || "en"}
                  onChange={switchLang}
                  aria-label={t("common.selectLanguage", "Select language")}
                >
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select> */}
              </div>
              {name && <span className="lms-portal-username">{name}</span>}
              <button className="lms-portal-logout lms-portal-logout-mobile" onClick={doLogout}>
                <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
                <span>{t("common.logout", "Log out")}</span>
              </button>
            </div>
          </nav>
          <div className="lms-portal-user">
            <div className="lms-portal-language">
              {/* <select
                className="policy-language-select lms-portal-policy-language-select"
                value={i18n.language || localStorage.getItem("userLanguage") || "en"}
                onChange={switchLang}
                aria-label={t("common.selectLanguage", "Select language")}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select> */}
            </div>
            {name && <span className="lms-portal-username">{name}</span>}
            <button className="lms-portal-logout" onClick={doLogout}>
              <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
              <span>{t("common.logout", "Log out")}</span>
            </button>
          </div>
        </header>
        <main className={`lms-portal-main ${wide ? "wide" : ""}`}>{children}</main>
      </div>
    </>
  );
};

/* Small shared bits used across portal pages */

export const ProgressBar = ({ percent = 0, small = false }) => {
  const { t } = useTranslation();
  const rounded = Math.round(percent);
  
  let bgColor = "#0dcaf0"; // default fallback
  if (rounded <= 25) bgColor = "#dc3545"; // red
  else if (rounded < 75) bgColor = "#ffc107"; // yellow
  else bgColor = "#28a745"; // green

  return (
    <div
      className={`lms-progressbar ${small ? "small" : ""}`}
      role="progressbar"
      aria-valuenow={rounded}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t("learner.lms.progress.ariaLabel", "{{percent}}% complete", { percent: rounded })}
    >
      <span style={{ width: `${Math.min(100, Math.max(0, percent))}%`, backgroundColor: bgColor }}></span>
    </div>
  );
};

export const LearnerStatusBadge = ({ status, t }) => {
  const s = (status || "").toLowerCase();
  let bg = "#f39c12"; // vibrant orange for not started or default
  if (s === "completed" || s === "passed") bg = "#28a745"; // solid green
  else if (s === "in_progress" || s === "active") bg = "#007bff"; // solid blue

  const label = t(`learner.lms.status.${s || "not_started"}`, (s || "not started").replace(/_/g, " "));
  return <span className="lms-portal-badge" style={{ backgroundColor: bg, color: 'white' }}>{label}</span>;
};

export const EmptyState = ({ icon = "fa-folder-open", text }) => (
  <div className="lms-portal-empty">
    <i className={`fas ${icon}`} aria-hidden="true"></i>
    <p>{text}</p>
  </div>
);

export default LearnerShell;
